import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { isKeycloakExchangeRequest } from './shared/services/keycloak-exchange.service';
import { KeycloakService } from './shared/services/keycloak.service';

/**
 * Attaches the Keycloak access token to the token-exchange endpoint, and to nothing else.
 *
 * The scope is ONE endpoint, not "our own API". That distinction is the whole point of this file:
 *
 *  1. The backend accepts the "Keycloak" authentication scheme on exactly two actions —
 *     AuthController.ExchangeKeycloakToken and KeycloakTestController. Every other endpoint uses
 *     [Authorize] with the default scheme, which validates the internal BackOffice JWT. Sending a
 *     Keycloak bearer to those endpoints does not authenticate the request, it BREAKS it: the
 *     header wins over the internal JWT and the endpoint answers 401. ErrorInterceptor then turns
 *     that 401 into a redirect to /auth, so a perfectly good SSO session looked like a failed
 *     login. An earlier version of this interceptor allow-listed everything under `apiUrl` and
 *     caused exactly that. Do not widen it back.
 *
 *  2. A bearer token sent to a cross-origin host leaks a credential to a third party. This app
 *     talks to altusentertainment.b-cdn.net, altus.primas.net:8030 and video hosts.
 *
 *  3. Adding a non-safelisted header to a cross-origin request forces a CORS preflight that CDNs
 *     do not answer. That exact mistake — ErrorInterceptor setting "CurrentRoute" on every
 *     request — produced net::ERR_FAILED on raw image downloads and was fixed in 754afc4ca5.
 *
 * Every other request is left untouched and is authorised by NbAuthJWTInterceptor with the
 * internal token, exactly as it was before SSO existed.
 */
@Injectable()
export class KeycloakInterceptor implements HttpInterceptor {
  constructor(private keycloakService: KeycloakService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!isKeycloakExchangeRequest(req.url) || !this.keycloakService.isAuthenticated) {
      return next.handle(req);
    }

    // No proactive updateToken() here on purpose.
    //
    // The exchange only ever runs immediately after KeycloakService.init() has minted a token on this
    // same page load, and that token lives 300s, so a refresh can never be the thing that saves this
    // request. It did, however, actively break it: keycloak-js guards refresh with
    // `if (!kc.refreshToken)`, which the literal STRING "undefined" passes, so the adapter posted
    // `grant_type=refresh_token&refresh_token=undefined` and Keycloak answered
    // 400 invalid_grant "Invalid refresh token" on every exchange attempt.
    //
    // Sending the token we already have is both simpler and strictly more correct.
    return next.handle(this.withBearer(req));
  }

  private withBearer(req: HttpRequest<any>): HttpRequest<any> {
    const token = this.keycloakService.token;

    // "undefined" and "null" are checked as STRINGS, not just for falsiness. keycloak-js was observed
    // building a request body containing the literal text `refresh_token=undefined`, so the adapter
    // demonstrably stringifies missing token state rather than leaving it nullish. A truthy
    // `"undefined"` here would sail past a plain `!token` check and go out as `Bearer undefined`,
    // which the backend rejects as 401 — indistinguishable from "this account has no BackOffice
    // access" and a guaranteed wrong diagnosis.
    if (!token || token === 'undefined' || token === 'null') {
      console.warn('[Keycloak] no usable access token for the exchange request'
        + ' | value=' + String(token) + ' — sending it unauthenticated so the 401 is honest');
      return req;
    }
    return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
  }
}
